/**
 * Auth Compatibility Hook
 * Provides a unified interface for both Firebase and Supabase authentication
 * Allows gradual migration from Firebase to Supabase
 */
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { UserRole } from '@/types';

export interface CompatibleAuthContext {
  user: any | null;
  profile: any | null;
  session: any | null;
  loading: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signInWithGoogle: () => Promise<any>;
  signOut: () => Promise<void>;
}

/**
 * Compatibility hook that wraps Supabase auth and provides the same interface
 * as the old UnifiedAuth system
 */
export const useAuthCompatibility = (): CompatibleAuthContext => {
  const supabaseAuth = useSupabaseAuth();

  // Map Supabase auth to UnifiedAuth interface
  return {
    user: supabaseAuth.user,
    profile: supabaseAuth.profile,
    session: supabaseAuth.session,
    loading: supabaseAuth.loading,
    isLoading: supabaseAuth.loading,
    isInitialized: !supabaseAuth.loading,
    isAuthenticated: !!supabaseAuth.user,

    // Role checking methods
    hasRole: (role: UserRole) => {
      if (!supabaseAuth.profile) return false;
      return supabaseAuth.profile.role === role;
    },

    hasAnyRole: (roles: UserRole[]) => {
      if (!supabaseAuth.profile) return false;
      return roles.includes(supabaseAuth.profile.role);
    },

    // Permission checking methods
    hasPermission: supabaseAuth.hasPermission,

    hasAnyPermission: (permissions: string[]) => {
      return permissions.some(permission => supabaseAuth.hasPermission(permission));
    },

    // Authentication methods
    signIn: supabaseAuth.signIn,
    signUp: supabaseAuth.signUp,
    signInWithGoogle: supabaseAuth.signInWithGoogle,
    signOut: supabaseAuth.signOut,
  };
};

/**
 * Alias for backward compatibility
 * This allows existing code using useSupabaseAuth to work without changes
 */
export const useSupabaseAuth = useAuthCompatibility;